import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  TrendingUp, Users, Target, Phone, Mail, MapPin, 
  CheckCircle, Star, BarChart3, Clock, Shield,
  ArrowRight, Building2, DollarSign, Zap
} from 'lucide-react';

const LeadOfferPage = () => {
  const [formData, setFormData] = useState({
    facilityName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    state: '',
    currentLeadVolume: '',
    targetLeadVolume: '',
    budget: '',
    specializations: [],
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitted(true);
    } catch (error) {
      alert('Error submitting request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const pricingTiers = [
    {
      name: "Starter",
      price: "$25",
      period: "per qualified lead",
      description: "Perfect for smaller facilities looking to grow",
      features: [
        "Pre-qualified leads within 24 hours",
        "Basic lead information & contact details",
        "Email delivery & phone notification",
        "30-day lead replacement guarantee",
        "Monthly reporting dashboard"
      ],
      volume: "5-25 leads/month",
      highlight: false
    },
    {
      name: "Growth",
      price: "$20",
      period: "per qualified lead",
      description: "Ideal for established facilities scaling up",
      features: [
        "Pre-qualified leads within 12 hours",
        "Detailed lead profiles & urgency scoring",
        "Real-time lead delivery & SMS alerts",
        "60-day lead replacement guarantee", 
        "Weekly reporting & lead analytics",
        "Dedicated account manager",
        "Custom lead filtering preferences"
      ],
      volume: "25-75 leads/month",
      highlight: true
    },
    {
      name: "Enterprise",
      price: "$15",
      period: "per qualified lead",
      description: "High-volume facilities & multi-location centers",
      features: [
        "Pre-qualified leads within 6 hours",
        "Comprehensive lead profiles & family contact info",
        "Instant delivery via API integration",
        "90-day lead replacement guarantee",
        "Real-time dashboard & advanced analytics",
        "Dedicated account team & priority support",
        "Custom lead criteria & exclusive territories",
        "White-label lead capture pages"
      ],
      volume: "75+ leads/month",
      highlight: false
    }
  ];

  const benefits = [
    {
      icon: <Target className="h-8 w-8 text-blue-600" />,
      title: "Pre-Qualified Leads",
      description: "Every lead is verified and screened for genuine treatment interest before delivery"
    },
    {
      icon: <Clock className="h-8 w-8 text-green-600" />,
      title: "Fast Delivery",
      description: "Get leads within 6-24 hours depending on your plan, with real-time notifications"
    },
    {
      icon: <Shield className="h-8 w-8 text-purple-600" />,
      title: "Quality Guarantee",
      description: "30-90 day lead replacement guarantee ensures you only pay for genuine prospects"
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-orange-600" />,
      title: "Performance Tracking",
      description: "Detailed analytics and reporting to track ROI and lead conversion rates"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      title: "Director, Hope Recovery Center",
      location: "California",
      rating: 5,
      text: "The lead quality has been exceptional. We've seen a 40% increase in admissions since partnering with Addiction Rehab Call. The leads are genuinely interested and ready for treatment."
    },
    {
      name: "Michael Brown", 
      title: "CEO, Serenity Treatment Network",
      location: "Texas",
      rating: 5,
      text: "What sets them apart is the speed and accuracy. We get leads that match our specializations perfectly, and the 24/7 delivery means we never miss an opportunity."
    },
    {
      name: "Dr. Lisa Chen",
      title: "Medical Director, New Beginnings Rehab",
      location: "New York", 
      rating: 5,
      text: "The ROI has been incredible. For every dollar we spend on leads, we're seeing $8-12 in revenue. The quality guarantee gives us confidence in our investment."
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <Card className="shadow-lg">
              <CardContent className="py-12">
                <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-6" />
                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                  Request Submitted Successfully!
                </h1>
                <p className="text-gray-600 mb-8">
                  Thank you for your interest in our lead generation services. Our team will review 
                  your information and contact you within 24 hours to discuss your specific needs 
                  and create a customized lead program for your facility.
                </p>
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">
                    <strong>Next Steps:</strong>
                  </p>
                  <ul className="text-sm text-gray-600 text-left max-w-md mx-auto space-y-2">
                    <li>• Lead specialist will call you within 24 hours</li>
                    <li>• Discuss your target patient demographics</li>
                    <li>• Set up lead delivery preferences</li>
                    <li>• Begin receiving qualified leads within 48 hours</li>
                  </ul>
                </div>
                <div className="mt-8 space-y-4">
                  <Button asChild className="w-full sm:w-auto">
                    <Link to="/facility/dashboard">Go to Dashboard</Link>
                  </Button>
                  <p className="text-sm text-gray-500">
                    Have questions? Call us at{' '}
                    <a href="tel:1-888-968-1014" className="text-blue-600 hover:underline font-medium">
                      1-888-968-1014
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-white/20 text-white border-0">
              Lead Generation Program
            </Badge>
            <h1 className="text-5xl font-bold mb-6">
              Get More <span className="text-yellow-300">Qualified Patients</span><br />
              For Your Treatment Facility
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Connect with people actively seeking addiction treatment in your area. 
              Pre-qualified leads delivered directly to you within 24 hours, with quality guarantees.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                size="lg" 
                className="bg-yellow-500 text-gray-900 hover:bg-yellow-400 font-semibold"
                onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
              >
                <Target className="h-5 w-5 mr-2" />
                Get Started Today
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-blue-600"
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              >
                View Pricing Plans
              </Button>
            </div>

            {/* Key Stats */}
            <div className="grid md:grid-cols-3 gap-8 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-300 mb-2">12,000+</div>
                <div className="text-blue-100">Leads Delivered</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-300 mb-2">89%</div>
                <div className="text-blue-100">Lead Quality Score</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-300 mb-2">24hrs</div>
                <div className="text-blue-100">Average Delivery Time</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Treatment Centers Choose Our Lead Program
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Get a steady stream of pre-qualified, treatment-ready individuals 
                delivered directly to your facility with performance guarantees.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <Card key={index} className="text-center border-0 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-8 pb-6">
                    <div className="flex justify-center mb-4">
                      {benefit.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                How Our Lead Program Works
              </h2>
              <p className="text-gray-600">
                Simple, transparent process to get you connected with qualified treatment seekers
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Lead Capture</h3>
                <p className="text-gray-600 text-sm">
                  People seeking treatment submit inquiries through our website, ads, and partner network
                </p>
              </div>

              <div className="text-center">
                <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Lead Qualification</h3>
                <p className="text-gray-600 text-sm">
                  Our team verifies insurance, treatment needs, and readiness level before delivery
                </p>
              </div>

              <div className="text-center">
                <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Lead Delivery</h3>
                <p className="text-gray-600 text-sm">
                  Qualified leads are delivered to matching facilities within 6-24 hours via phone, email, or API
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Choose Your Lead Program
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Flexible pricing plans designed to fit facilities of all sizes. 
                Pay only for qualified leads with our satisfaction guarantee.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {pricingTiers.map((tier, index) => (
                <Card 
                  key={index} 
                  className={`relative ${tier.highlight ? 'border-blue-500 border-2 scale-105' : 'border-gray-200'} shadow-lg`}
                >
                  {tier.highlight && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-blue-600 text-white">Most Popular</Badge>
                    </div>
                  )}
                  
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-2xl font-bold text-gray-900">
                      {tier.name}
                    </CardTitle>
                    <div className="text-4xl font-bold text-blue-600 mt-2">
                      {tier.price}
                      <span className="text-lg text-gray-500 font-normal">/{tier.period}</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-2">{tier.description}</p>
                    <div className="bg-gray-100 rounded-lg px-3 py-2 mt-4">
                      <span className="text-sm font-medium text-gray-700">{tier.volume}</span>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <ul className="space-y-3">
                      {tier.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      className={`w-full mt-6 ${tier.highlight ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                      onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Get Started with {tier.name}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-8">
              <p className="text-gray-600 text-sm">
                All plans include lead replacement guarantee and dedicated support. 
                <a href="tel:1-888-968-1014" className="text-blue-600 hover:underline ml-1">
                  Call 1-888-968-1014 for custom enterprise pricing.
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What Our Partner Facilities Say
              </h2>
              <p className="text-gray-600">
                Real results from treatment centers using our lead generation program
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <Card key={index} className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <p className="text-gray-600 mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.title}</div>
                      <div className="text-sm text-blue-600">{testimonial.location}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Start Getting More Patients Today
              </h2>
              <p className="text-gray-600">
                Fill out the form below and our lead specialist will contact you within 24 hours 
                to discuss your specific needs and set up your lead program.
              </p>
            </div>

            <Card className="shadow-lg">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="facilityName">Facility Name *</Label>
                      <Input
                        id="facilityName"
                        value={formData.facilityName}
                        onChange={(e) => handleInputChange('facilityName', e.target.value)}
                        placeholder="Your Treatment Center"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="contactName">Contact Name *</Label>
                      <Input
                        id="contactName"
                        value={formData.contactName}
                        onChange={(e) => handleInputChange('contactName', e.target.value)}
                        placeholder="Your full name"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="your@email.com"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="(555) 123-4567"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        type="url"
                        value={formData.website}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                        placeholder="https://yourfacility.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Select value={formData.state} onValueChange={(value) => handleInputChange('state', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your state" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CA">California</SelectItem>
                          <SelectItem value="TX">Texas</SelectItem>
                          <SelectItem value="FL">Florida</SelectItem>
                          <SelectItem value="NY">New York</SelectItem>
                          <SelectItem value="PA">Pennsylvania</SelectItem>
                          <SelectItem value="IL">Illinois</SelectItem>
                          <SelectItem value="OH">Ohio</SelectItem>
                          <SelectItem value="other">Other State</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="currentLeadVolume">Current Monthly Lead Volume</Label>
                      <Select value={formData.currentLeadVolume} onValueChange={(value) => handleInputChange('currentLeadVolume', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select current volume" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0-5">0-5 leads/month</SelectItem>
                          <SelectItem value="5-20">5-20 leads/month</SelectItem>
                          <SelectItem value="20-50">20-50 leads/month</SelectItem>
                          <SelectItem value="50+">50+ leads/month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="targetLeadVolume">Target Monthly Lead Volume *</Label>
                      <Select value={formData.targetLeadVolume} onValueChange={(value) => handleInputChange('targetLeadVolume', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select target volume" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5-25">5-25 leads/month</SelectItem>
                          <SelectItem value="25-75">25-75 leads/month</SelectItem>
                          <SelectItem value="75-150">75-150 leads/month</SelectItem>
                          <SelectItem value="150+">150+ leads/month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="budget">Monthly Lead Budget</Label>
                    <Select value={formData.budget} onValueChange={(value) => handleInputChange('budget', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="500-1000">$500 - $1,000</SelectItem>
                        <SelectItem value="1000-2500">$1,000 - $2,500</SelectItem>
                        <SelectItem value="2500-5000">$2,500 - $5,000</SelectItem>
                        <SelectItem value="5000+">$5,000+</SelectItem>
                        <SelectItem value="discuss">Discuss with specialist</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message">Additional Information</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Tell us about your facility specializations, target demographics, or any specific requirements..."
                      rows={4}
                    />
                  </div>

                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertDescription>
                      Your information is secure and will only be used to provide you with lead generation services. 
                      We never share facility data with competitors.
                    </AlertDescription>
                  </Alert>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      type="submit" 
                      size="lg" 
                      disabled={isSubmitting}
                      className="bg-blue-600 hover:bg-blue-700 flex-1"
                    >
                      {isSubmitting ? (
                        <>Processing...</>
                      ) : (
                        <>
                          <Target className="h-5 w-5 mr-2" />
                          Start My Lead Program
                        </>
                      )}
                    </Button>
                    
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="lg"
                      asChild
                    >
                      <a href="tel:1-888-968-1014">
                        <Phone className="h-5 w-5 mr-2" />
                        Call: 1-888-968-1014
                      </a>
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How do you qualify leads?</h3>
                <p className="text-gray-600 text-sm mb-6">
                  We verify insurance coverage, treatment readiness, location, and specific needs before delivery. 
                  Only leads that meet your criteria are sent to you.
                </p>

                <h3 className="font-semibold text-gray-900 mb-2">What's included in the replacement guarantee?</h3>
                <p className="text-gray-600 text-sm mb-6">
                  If a lead doesn't meet quality standards or fails to respond within the guarantee period, 
                  we'll replace it at no charge.
                </p>

                <h3 className="font-semibold text-gray-900 mb-2">Can I customize my lead criteria?</h3>
                <p className="text-gray-600 text-sm">
                  Yes! We can filter leads by insurance type, treatment level needed, demographics, 
                  distance from your facility, and specialization requirements.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How quickly will I receive leads?</h3>
                <p className="text-gray-600 text-sm mb-6">
                  Leads are delivered within 6-24 hours depending on your plan. Enterprise clients 
                  receive near real-time delivery via API integration.
                </p>

                <h3 className="font-semibold text-gray-900 mb-2">What payment options are available?</h3>
                <p className="text-gray-600 text-sm mb-6">
                  We offer monthly billing, prepaid credit systems, or pay-per-lead options. 
                  No setup fees or long-term contracts required.
                </p>

                <h3 className="font-semibold text-gray-900 mb-2">Do you serve all states?</h3>
                <p className="text-gray-600 text-sm">
                  Yes, we deliver leads nationwide. Some rural areas may have limited volume, 
                  which we'll discuss during your consultation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeadOfferPage;